import {Component, DestroyRef, inject, Input, OnDestroy, OnInit} from '@angular/core';
import {
  BokkzVpbDataService,
  CorporateTaxEntity,
  VpbBerekening
} from "@snelstart-ventures/fiscaal/domain-logic";
import {FormControl, FormGroup} from "@angular/forms";
import {debounceTime, distinctUntilChanged, filter, Observable, shareReplay, switchMap, tap} from "rxjs";
import {SharedUiDetailsModule} from '@snelstart/ui-details';
import {CommonModule, DecimalPipe} from '@angular/common';
import {SharedUiFormControlsModule} from '@snelstart/ui-form-controls';
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";
import {SharedUiAlertModule} from "@snelstart/ui-alert";

@Component({
  selector: 'snelstart-venture-fiscaal-feature-overzicht',
  templateUrl: './overzicht.component.html',
  styleUrls: ['./overzicht.component.scss'],
  imports: [
    SharedUiDetailsModule,
    DecimalPipe,
    SharedUiFormControlsModule,
    CommonModule,
    SharedUiAlertModule
  ]
})
export class OverzichtComponent implements OnInit, OnDestroy {

  @Input() public id!: string;
  private dataService = inject(BokkzVpbDataService);
  private destroyRef = inject(DestroyRef);

  readonly corporateTaxEntity$: Observable<CorporateTaxEntity> =
    this.dataService.corporateTaxEntity$.pipe(
      filter((data): data is CorporateTaxEntity => !!data?.sstRelationId),
      tap(data => {
        if (data.aangifte?.verliescompensatie) {
          this.verliesCompensatieFg.patchValue(
            { verrekeningVerliesDitBoekjaar: data.aangifte.verliescompensatie.verrekeningVerliesDitBoekjaar },
            { emitEvent: false }
          );
        }
      }),
      takeUntilDestroyed(this.destroyRef),
      shareReplay({bufferSize: 1, refCount: true})
    );

  readonly vpbBerekening$: Observable<VpbBerekening> =
    this.dataService.vpbBerekening$.pipe(
      filter((data): data is VpbBerekening => data != null),
      tap(data => {
        this.belastbareWinstFg.patchValue(data.belastbareWinst!, {emitEvent: false});
        this.belastbaarBedragFg.patchValue(data.belastbaarBedrag!, {emitEvent: false});
      }),
      takeUntilDestroyed(this.destroyRef),
      shareReplay({bufferSize: 1, refCount: true})
    );


  public belastbareWinstFg = new FormGroup({
    saldoFiscaleWinst: new FormControl<number | null>(null),
    eliminatieVerliesBoekjaar: new FormControl<number | null>(null),
    imputatieVerlies: new FormControl<number | null>(null),
    vrijgesteldeWinstbestanddelen: new FormControl<number | null>(0),
    objectvrijstellingBuitenlandseWinst: new FormControl<number | null>(null),
    verliesbruteringBuitenlandseBeleggingsonderneming: new FormControl<number | null>(null),
    nietAftrekbareBoetes: new FormControl<number | null>(0),
    beperktAftrekbareKosten: new FormControl<number | null>(0),
    winstUitZeescheepvaart: new FormControl<number | null>(null),
    fondswerversWinst: new FormControl<number | null>(null),
    fictievePersooneelskostenANBI: new FormControl<number | null>(null),
    totaalbedragKiaInNederland: new FormControl<number | null>(null),
    totaalbedragKiaCaribischDeel: new FormControl<number | null>(null),
    totaalKiaVoorgaandeJaren: new FormControl<number | null>(null),
    totaleKiaDitJaar: new FormControl<number | null>(null),
    investeringsregelingenTotaal: new FormControl<number | null>(0),
    toevoegingKER: new FormControl<number | null>(null),
    onttrekkingKER: new FormControl<number | null>(null),
    toevoegingHIR: new FormControl<number | null>(null),
    onttrekkingHIR: new FormControl<number | null>(null),
    toevoegingCompartimenteringsreserve: new FormControl<number | null>(null),
    onttrekkingCompartimenteringsreserve: new FormControl<number | null>(null),
    toevoegingOverigeReserves: new FormControl<number | null>(null),
    onttrekkingOverigeReserves: new FormControl<number | null>(null),
    totaalToevoegingenReserves: new FormControl<number | null>(null),
    totaalOnttrekkingReserves: new FormControl<number | null>(null),
    mutatieReserves: new FormControl<number | null>(0),
    renteEnRoyaltiesDoorstroomvennootschappen: new FormControl<number | null>(null),
    nietAfrekbareRente: new FormControl<number | null>(null),
    bovenmatigeDeelnemingsrente: new FormControl<number | null>(null),
    renteOvernameschulden: new FormControl<number | null>(null),
    totaalNietAfrekbareRente: new FormControl<number | null>(null),
    aftrekGetemporiseerdeRente: new FormControl<number | null>(null),
    totaalGeneriekeRenteAftrekBeperking: new FormControl<number | null>(null),
    nietInAanmerkingTeNemenVoordeelInnovatiebox: new FormControl<number | null>(0),
    terugnameVoordeelInnovatiebox: new FormControl<number | null>(0),
    saldoVoordeelInnovatiebox: new FormControl<number | null>(0),
    liquidatieverlies: new FormControl<number | null>(null),
    toevoegingAanWinstOpGrondVanArtikel13c: new FormControl<number | null>(null),
    toevoegingAanWinstOpGrondVanArtikel13h: new FormControl<number | null>(null),
    toevoegingAanWinstOpGrondVanArtikel13lid17: new FormControl<number | null>(null),
    saldoNietVrijgesteldeVoordelenUitDeelneming: new FormControl<number | null>(null),
    nettoVoordelenUitDeelneming: new FormControl<number | null>(null),
    voordelenUitNietKwalificerendeDeelnemingen: new FormControl<number | null>(null),
    deelnemingsvrijstelling: new FormControl<number | null>(0),
    maximaalAftrekbareGiften: new FormControl<number | null>(null),
    giftenAanCultureleInstellingen: new FormControl<number | null>(0),
    giftenAanOverigeInstellingen: new FormControl<number | null>(0),
    extraAftrekCultureleInstellingen: new FormControl<number | null>(0),
    aftrekbareGiften: new FormControl<number | null>(0),
    belastbareWinst: new FormControl<number | null>(null)
  });

  public belastbaarBedragFg = new FormGroup({
    winstVoorSplitsingBelangenwijziging: new FormControl<number | null>(0),
    belastbaarInkomenAbNederlandseVennootschap: new FormControl<number | null>(0),
    belastbareWinstNederlandseOnderneming: new FormControl<number | null>(0),
    belastbareWinst:  new FormControl<number | null>(0),
    teVerrekenenGewoneVerliezenDitBoekjaar: new FormControl<number | null>(0),
    teVerrekenenHoudsterverliezenDitBoekjaar: new FormControl<number | null>(0),
    totaalTeVerrekenenVerlies: new FormControl<number | null>(0),
    belastbaarBedrag: new FormControl<number | null>(0),
    omrekenfactorFunctioneleValuta: new FormControl<string | null>(null),
    grondslagFunctioneleValuta: new FormControl<number | null>(0),
    nogTeVerrekenenGewoneVerliezen: new FormControl<number | null>(0),
    nogTeVerrekenenHoudsterVerliezen: new FormControl<number | null>(0),
  });

  public verliesCompensatieFg = new FormGroup({
    verrekeningVerliesDitBoekjaar: new FormControl<number | null>(null),
  });

  public tellingenFg = new FormGroup({
    restantVerliesDitBoekjaar: new FormControl<number | null>(null),
    tellingWinstCorrectiesAftrek: new FormControl<number | null>(null),
    tellingWinstCorrectiesBijtel: new FormControl<number | null>(null)
  });

  ngOnInit(): void {
    this.verliesCompensatieFg.controls.verrekeningVerliesDitBoekjaar.valueChanges.pipe(
      debounceTime(500),
      distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b)),
      tap(() => this.saveForm()),
      switchMap(() => this.dataService.calculateLatestCorporateTaxReturn()),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe();
  }

  ngOnDestroy(): void {
    this.saveForm();
  }

  saveForm(): void {
    this.verliesCompensatieFg.updateValueAndValidity();

    const verliesCompensatie = this.verliesCompensatieFg.getRawValue();

    this.dataService.patchCorporateTaxEntity(currentEntity => {
      if (!currentEntity.aangifte) {
        return currentEntity;
      }

      return {
        ...currentEntity,
        aangifte: {
          ...currentEntity.aangifte,
          verliescompensatie: {
            ...currentEntity.aangifte.verliescompensatie,
            verrekeningVerliesDitBoekjaar: verliesCompensatie.verrekeningVerliesDitBoekjaar ?? undefined,
          },
        },
      };
    });
  }

  public updateRestantVerlies(): number {
    const belastbaarBedrag = this.belastbaarBedragFg.getRawValue().belastbaarBedrag || 0;
    const verrekeningVerlies = this.verliesCompensatieFg.controls.verrekeningVerliesDitBoekjaar.value || 0;
    const restant = belastbaarBedrag + verrekeningVerlies;
    this.tellingenFg.controls.restantVerliesDitBoekjaar.setValue(restant, { emitEvent: false });
    return restant;
  }

  public optellenWinstCorrecties(): any {
    const saldoFiscaleWinst = this.belastbareWinstFg.getRawValue().saldoFiscaleWinst || 0;
    const belastbareWinst = this.belastbaarBedragFg.getRawValue().belastbareWinst || 0;
    const verschil = saldoFiscaleWinst - belastbareWinst;
    if (verschil >= 0) {
      this.tellingenFg.controls.tellingWinstCorrectiesAftrek.setValue(verschil, { emitEvent: false });
      return verschil;
    } else {
      const positiefVerschil = verschil * -1;
      this.tellingenFg.controls.tellingWinstCorrectiesBijtel.setValue(positiefVerschil, { emitEvent: false });
      return positiefVerschil;
    }
  }
}
